import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Plus, Trash2, Upload, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import { Invoice, InvoiceItem } from '../../types';
import { generateInvoicePDF } from '../../utils/pdfGenerator';
import { useAppStore } from '../../store';
import ErrorModal from './ErrorModal';
import { useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import { getCurrentArgentinaISOString } from '../../utils/dateUtils';
import { getNextInvoiceNumber } from '../../utils/numberGenerator';
import { useOnOpen } from '../../hooks/useOnOpen';

interface InvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  entity_id: string;
  entity_type: 'client' | 'supplier';
  entityName: string;
}

const IVA_OPTIONS = [0, 10.5, 21];
const RECARGO_OPTIONS = [0, 2, 3, 5, 10];
const INVOICE_TYPES = [
  { value: 'A' as const, label: 'A' },
  { value: 'B' as const, label: 'B' },
  { value: 'C' as const, label: 'C' },
  { value: 'X' as const, label: 'X' },
];
const SALE_TYPE_OPTIONS = [
  { value: 'production', label: 'Producción' },
  { value: 'sample', label: 'Muestra' },
  { value: 'express', label: 'Pedido Express' },
  { value: 'express_sample', label: 'Muestra Express' },
  { value: 'notmerch', label: 'Notmerch' },
  { value: 'other', label: 'Otros' },
];

const InvoiceModal: React.FC<InvoiceModalProps> = ({
  isOpen,
  onClose,
  entity_id,
  entity_type,
  entityName,
}) => {
  const { addInvoice, invoices, salespersons, clients } = useAppStore();
  const [items, setItems] = useState<Omit<InvoiceItem, 'id'>[]>([]);
  const [invoiceType, setInvoiceType] = useState<'A' | 'B' | 'C' | 'X'>('X');
  const [date, setDate] = useState(getCurrentArgentinaISOString());
  const [dueDate, setDueDate] = useState('');
  const [issued_in_afip, setIssuedInAfip] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [manualNumber, setManualNumber] = useState(false);
  const [reference, setReference] = useState('');
  const [invoiceFile, setInvoiceFile] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isCheckingNumber, setIsCheckingNumber] = useState(false);
  const [salesperson, setSalesperson] = useState<string | null>(null);
  const [currency, setCurrency] = useState<'ARS' | 'USD'>('ARS');
  const [exchangeRate, setExchangeRate] = useState<string>('1480');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [saleType, setSaleType] = useState<'production' | 'sample' | 'express' | 'express_sample' | 'notmerch' | 'other' | ''>('');

  const salespersonOptions = useMemo(() => {
    const validNames = ['Mateo', 'Male', 'Ori', 'Otros'];
    const validSalespersons = salespersons.filter(sp =>
      validNames.includes(sp.name) || sp.name.match(/^[a-zA-Z]+$/)
    );
    return [
      { value: '', label: 'Ninguno' },
      ...validSalespersons.map(sp => ({ value: sp.name, label: sp.name })),
    ];
  }, [salespersons]);

  useOnOpen(isOpen, () => {
    setInvoiceType('X');
    setItems([{ description: '', quantity: 1, unit_price: 0, tax_rate: 0, total: 0 }]);
    setDate(getCurrentArgentinaISOString());
    setDueDate('');
    setIssuedInAfip(false);
    setInvoiceNumber('');
    setManualNumber(false);
    setReference('');
    setInvoiceFile(null);
    setShowAdvanced(false);
    setSaleType('');
    let defaultSalesperson: string | null = null;
    if (entity_type === 'client' && entity_id) {
      const c = clients.find((x) => x.id === entity_id);
      const sp = c?.salesperson?.trim();
      if (sp) defaultSalesperson = sp;
    }
    setSalesperson(defaultSalesperson);
  });

  const defaultTaxRate = invoiceType === 'X' ? 0 : 21;
  const addItem = () => {
    setItems([...items, { description: '', quantity: 1, unit_price: 0, tax_rate: defaultTaxRate, total: 0 }]);
  };

  const updateItem = (index: number, field: keyof InvoiceItem, value: any) => {
    setItems(items.map((item, i) => {
      if (i !== index) return item;
      const updated = { ...item, [field]: value };
      if (field === 'quantity' || field === 'unit_price' || field === 'tax_rate') {
        const q = Math.max(0, updated.quantity);
        const p = Math.max(0, updated.unit_price);
        const t = Math.max(0, updated.tax_rate);
        updated.quantity = q;
        updated.unit_price = p;
        updated.tax_rate = t;
        updated.total = q * p * (1 + t / 100);
      }
      return updated;
    }));
  };

  const removeItem = (index: number) => {
    if (items.length <= 1) return;
    setItems(items.filter((_, i) => i !== index));
  };

  const { subtotal, tax, total } = useMemo(() => {
    const st = items.reduce((s, i) => s + i.quantity * i.unit_price, 0);
    const tx = items.reduce((s, i) => s + (i.quantity * i.unit_price * i.tax_rate / 100), 0);
    return { subtotal: st, tax: tx, total: st + tx };
  }, [items]);

  const handleFileUpload = async (file: File) => {
    try {
      setIsUploading(true);
      const ext = file.name.split('.').pop();
      const path = `invoices/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
      const { error: uploadError } = await supabase.storage.from('assets').upload(path, file, { cacheControl: '3600', upsert: false });
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage.from('assets').getPublicUrl(path);
      setInvoiceFile(publicUrl);
    } catch (err: any) {
      setError(err.message || 'Error al subir');
      setShowErrorModal(true);
    } finally {
      setIsUploading(false);
    }
  };

  const checkInvoiceNumber = (n: string) => invoices.some(inv => inv.number.toLowerCase() === n.toLowerCase());
  const generateUniqueInvoiceNumber = async () => {
    try {
      return await getNextInvoiceNumber(invoiceType);
    } catch {
      let c = 1;
      let cand = `${invoiceType}-${c.toString().padStart(8, '0')}`;
      while (checkInvoiceNumber(cand)) { c++; cand = `${invoiceType}-${c.toString().padStart(8, '0')}`; }
      return cand;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validItems = items.filter(i => i.description.trim());
    if (validItems.length === 0) {
      setError('Agregá al menos un ítem con descripción');
      setShowErrorModal(true);
      return;
    }

    if (subtotal < 0 || tax < 0 || total < 0) {
      setError('Los montos no pueden ser negativos');
      setShowErrorModal(true);
      return;
    }

    if (manualNumber) {
      if (!invoiceNumber.trim()) {
        setError('Ingresá el número de factura');
        setShowErrorModal(true);
        return;
      }
      setIsCheckingNumber(true);
      if (checkInvoiceNumber(invoiceNumber)) {
        setIsCheckingNumber(false);
        setError('Ya existe una factura con ese número');
        setShowErrorModal(true);
        return;
      }
      setIsCheckingNumber(false);
    }

    // Validar tipo de venta obligatorio para clientes
    if (entity_type === 'client' && !saleType) {
      setError('Seleccioná el tipo de venta (Producción, Muestra, Express o Muestra Express)');
      setShowErrorModal(true);
      return;
    }

    const finalNumber = manualNumber ? invoiceNumber : await generateUniqueInvoiceNumber();
    const rate = currency === 'USD' ? Math.max(0.000001, parseFloat(exchangeRate) || 0) : 1;

    const invoice: Omit<Invoice, 'id'> = {
      number: finalNumber,
      date,
      due_date: dueDate || null,
      entity_id,
      entity_type,
      type: invoiceType,
      items: validItems,
      subtotal,
      tax,
      total,
      balance: total,
      issued_in_afip,
      reference: reference || null,
      invoice_file: invoiceFile || null,
      salesperson: salesperson,
      currency,
      exchange_rate: rate,
      subtotal_ars: parseFloat((subtotal * rate).toFixed(2)),
      tax_ars: parseFloat((tax * rate).toFixed(2)),
      total_ars: parseFloat((total * rate).toFixed(2)),
      balance_ars: parseFloat((total * rate).toFixed(2)),
      sale_type: entity_type === 'client' ? (saleType as 'production' | 'sample' | 'express' | 'express_sample' | 'other') : undefined,
    };

    try {
      await addInvoice(invoice, validItems);
      if (entity_type === 'client' && invoiceType === 'X') {
        await generateInvoicePDF(invoice as Invoice, entityName);
      }
      onClose();
    } catch (err: any) {
      setError(err.message?.includes('duplicate') ? 'Ya existe una factura con ese número' : err.message || 'Error al crear');
      setShowErrorModal(true);
    }
  };

  const rate = parseFloat(exchangeRate) || 0;
  const totalARS = currency === 'USD' ? total * rate : total;

  return (
    <>
      <Dialog.Root open={isOpen} onOpenChange={onClose}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50" />
          <Dialog.Content className="fixed left-1/2 top-1/2 w-[95vw] max-w-xl -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white dark:bg-gray-900 shadow-xl overflow-hidden focus:outline-none">
            {/* Header compacto */}
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {entity_type === 'client' ? 'Nueva factura' : 'Nueva compra'}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{entityName}</p>
                </div>
                <Dialog.Close asChild>
                  <button className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-600" aria-label="Cerrar">
                    <X className="h-5 w-5" />
                  </button>
                </Dialog.Close>
              </div>

              {/* Tipo + Fecha en una fila */}
              <div className="flex flex-wrap gap-3 mt-4">
                <div className="flex gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg">
                  {INVOICE_TYPES.map(({ value, label }) => (
                    <button
                      key={value}
                      type="button"
                      onClick={() => setInvoiceType(value)}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        invoiceType === value
                          ? 'bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 shadow-sm'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
                />
              </div>

              {/* Tipo de venta (solo para clientes) */}
              {entity_type === 'client' && (
                <div className="mt-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Tipo de venta</span>
                    <span className="text-xs text-red-500">*</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {SALE_TYPE_OPTIONS.map(({ value, label }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setSaleType(value as typeof saleType)}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border ${
                          saleType === value
                            ? 'bg-indigo-600 text-white border-indigo-600'
                            : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700 hover:border-indigo-300'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[65vh]">
              {/* Ítems compactos */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ítems</span>
                  <button type="button" onClick={addItem} className="flex items-center gap-1.5 text-sm text-indigo-600 dark:text-indigo-400 hover:underline">
                    <Plus className="h-4 w-4" /> Agregar
                  </button>
                </div>

                {items.map((item, idx) => (
                  <div key={idx} className="flex gap-2 items-center flex-wrap">
                    <input
                      type="text"
                      placeholder="Descripción"
                      value={item.description}
                      onChange={(e) => updateItem(idx, 'description', e.target.value)}
                      className="flex-1 min-w-[140px] px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm placeholder-gray-400"
                    />
                    <input
                      type="number"
                      min="0"
                      step="1"
                      placeholder="Cant"
                      value={item.quantity || ''}
                      onChange={(e) => updateItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                      className="w-16 px-2 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-right"
                    />
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Precio"
                      value={item.unit_price || ''}
                      onChange={(e) => updateItem(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                      className="w-24 px-2 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-right"
                    />
                    {(() => {
                      const opts = invoiceType === 'X' ? RECARGO_OPTIONS : IVA_OPTIONS;
                      const isCustom = !opts.includes(item.tax_rate);
                      return (
                        <div className="flex flex-col gap-0.5">
                          <select
                            value={isCustom ? 'other' : item.tax_rate}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === 'other') {
                                const opts2 = invoiceType === 'X' ? RECARGO_OPTIONS : IVA_OPTIONS;
                                const val = opts2.includes(item.tax_rate) ? 0.01 : item.tax_rate;
                                updateItem(idx, 'tax_rate', val);
                                return;
                              }
                              updateItem(idx, 'tax_rate', parseFloat(v));
                            }}
                            className="w-20 px-2 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm"
                          >
                            {opts.map(v => (
                              <option key={v} value={v}>{v}%</option>
                            ))}
                            <option value="other">Otro</option>
                          </select>
                          {isCustom && (
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              placeholder="%"
                              value={item.tax_rate}
                              onChange={(e) => updateItem(idx, 'tax_rate', parseFloat(e.target.value) || 0)}
                              className="w-20 px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-right mt-0.5"
                            />
                          )}
                        </div>
                      );
                    })()}
                    <span className="w-20 text-sm font-medium text-gray-700 dark:text-gray-300 text-right">
                      ${item.total.toFixed(0)}
                    </span>
                    <button type="button" onClick={() => removeItem(idx)} className="p-1.5 text-gray-400 hover:text-red-500 rounded" disabled={items.length === 1}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>

              {/* Totales fijos */}
              <div className="mt-4 p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-800">
                <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400">
                  <span>Subtotal</span>
                  <span>${subtotal.toFixed(2)} {currency}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-500 dark:text-gray-400 mt-1">
                  <span>{invoiceType === 'X' ? 'Recargo' : 'IVA'}</span>
                  <span>${tax.toFixed(2)} {currency}</span>
                </div>
                <div className="flex justify-between text-base font-semibold text-gray-900 dark:text-white mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                  <span>Total</span>
                  <span>${total.toFixed(2)} {currency}</span>
                </div>
                {currency === 'USD' && (
                  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                    <span>≈ ARS</span>
                    <span>${totalARS.toFixed(0)}</span>
                  </div>
                )}
              </div>

              {/* Más opciones colapsable */}
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  {showAdvanced ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  Más opciones
                </button>
                {showAdvanced && (
                  <div className="mt-3 p-4 rounded-xl border border-gray-200 dark:border-gray-700 space-y-3">
                    <Input type="date" label="Vencimiento" id="due" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
                    <Select label="Moneda" value={currency} onChange={(v) => setCurrency(v as 'ARS' | 'USD')} options={[{ value: 'ARS', label: 'ARS' }, { value: 'USD', label: 'USD' }]} />
                    {currency === 'USD' && (
                      <Input type="number" label="Tipo de cambio" value={exchangeRate} onChange={(e) => setExchangeRate(e.target.value)} min="0" step="0.01" />
                    )}
                    <div className="flex items-center gap-2">
                      <input type="checkbox" id="manualNum" checked={manualNumber} onChange={(e) => setManualNumber(e.target.checked)} className="rounded" />
                      <label htmlFor="manualNum" className="text-sm">Número manual</label>
                    </div>
                    {manualNumber && (
                      <Input label="Número" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} placeholder="A-00000001" />
                    )}
                    <Input label="Referencia" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Opcional" />
                    {entity_type === 'client' && (
                      <Select label="Vendedor" value={salesperson || ''} onChange={(v) => setSalesperson(v || null)} options={salespersonOptions} />
                    )}
                    {entity_type === 'client' && (invoiceType === 'A' || invoiceType === 'B' || invoiceType === 'C') && (
                      <div className="flex items-center gap-2">
                        <input type="checkbox" id="afip" checked={issued_in_afip} onChange={(e) => setIssuedInAfip(e.target.checked)} className="rounded" />
                        <label htmlFor="afip" className="text-sm">Emitida en AFIP</label>
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Archivo (opcional)</label>
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" id="inv-file" onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0])} />
                      <Button type="button" variant="outline" size="sm" disabled={isUploading} icon={isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} onClick={() => document.getElementById('inv-file')?.click()}>
                        {invoiceFile ? 'Archivo subido' : 'Subir archivo'}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Acciones */}
              <div className="mt-6 flex gap-3">
                <Button type="button" variant="outline" onClick={onClose} className="flex-1">
                  Cancelar
                </Button>
                <Button type="submit" disabled={items.filter(i => i.description.trim()).length === 0 || isUploading || isCheckingNumber} className="flex-1">
                  Crear {entity_type === 'client' ? 'factura' : 'compra'}
                </Button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <ErrorModal isOpen={showErrorModal} onClose={() => setShowErrorModal(false)} message={error || 'Error'} />
    </>
  );
};

export default InvoiceModal;
